import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Badge } from './ui/badge';
import { AlertTriangle, KeyRound, Loader2, Plus, Save, Trash2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function VoiceAIKeysAdminSettings() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [featureEnabled, setFeatureEnabled] = useState(false);
  const [settings, setSettings] = useState({
    provider: 'elevenlabs',
    llm_provider: 'openai',
    llm_model: 'gpt-4o-mini',
    live_transfer_number: '',
    channels: {},
    use_cases: {},
    credentials_input: {
      elevenlabs_api_key: '',
      llm_api_key: '',
    },
    credentials_summary: {
      has_elevenlabs_api_key: false,
      has_llm_api_key: false,
      masked_elevenlabs_api_key: '',
      masked_llm_api_key: '',
    },
    additional_providers: [],
  });

  const getHeaders = useCallback(() => {
    const token = localStorage.getItem('dme_token');
    return { Authorization: `Bearer ${token}` };
  }, []);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [featuresRes, settingsRes] = await Promise.all([
        axios.get(`${API_URL}/api/features`, { headers: getHeaders() }),
        axios.get(`${API_URL}/api/dev-settings/voice-ai`, { headers: getHeaders() }),
      ]);
      const features = featuresRes.data?.features || {};
      setFeatureEnabled(features.voice_ai_leadgen === true);

      const cfg = settingsRes.data?.settings || {};
      setSettings((prev) => ({
        ...prev,
        provider: cfg.provider || prev.provider,
        llm_provider: cfg.llm_provider || prev.llm_provider,
        llm_model: cfg.llm_model || prev.llm_model,
        live_transfer_number: cfg.live_transfer_number || '',
        channels: cfg.channels || {},
        use_cases: cfg.use_cases || {},
        credentials_summary: cfg.credentials_summary || prev.credentials_summary,
        additional_providers: (cfg.additional_providers || []).map((item) => ({
          provider_name: item.provider_name || '',
          provider_type: item.provider_type || 'llm',
          api_key: '',
          has_api_key: item.has_api_key,
          masked_api_key: item.masked_api_key,
        })),
      }));
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to load Voice AI key settings');
    } finally {
      setLoading(false);
    }
  }, [getHeaders]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const addProvider = () => {
    setSettings((prev) => ({
      ...prev,
      additional_providers: [...prev.additional_providers, { provider_name: '', provider_type: 'llm', api_key: '' }],
    }));
  };

  const removeProvider = (index) => {
    setSettings((prev) => ({
      ...prev,
      additional_providers: prev.additional_providers.filter((_, idx) => idx !== index),
    }));
  };

  const saveSettings = async () => {
    try {
      setSaving(true);
      await axios.put(
        `${API_URL}/api/dev-settings/voice-ai`,
        {
          provider: settings.provider,
          llm_provider: settings.llm_provider,
          llm_model: settings.llm_model,
          live_transfer_number: settings.live_transfer_number,
          channels: settings.channels,
          use_cases: settings.use_cases,
          credentials: settings.credentials_input,
          additional_providers: settings.additional_providers,
        },
        { headers: getHeaders() }
      );
      toast.success('Voice AI keys/settings saved');
      setSettings((prev) => ({
        ...prev,
        credentials_input: { elevenlabs_api_key: '', llm_api_key: '' },
      }));
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save Voice AI key settings');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="p-8 flex justify-center" data-testid="voice-ai-keys-loading"><Loader2 className="w-7 h-7 animate-spin" /></div>;
  }

  return (
    <Card data-testid="voice-ai-keys-admin-settings-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2"><KeyRound className="w-5 h-5" /> Voice AI Provider Keys</CardTitle>
        <CardDescription>Store ElevenLabs + LLM keys here (Admin Settings), while Dev Settings controls feature toggle.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex gap-2 flex-wrap">
          <Badge variant={featureEnabled ? 'default' : 'secondary'} data-testid="voice-ai-keys-feature-badge">Feature: {featureEnabled ? 'Enabled' : 'Disabled'}</Badge>
          <Badge variant="outline" data-testid="voice-ai-keys-elevenlabs-summary">ElevenLabs Key: {settings.credentials_summary?.has_elevenlabs_api_key ? settings.credentials_summary?.masked_elevenlabs_api_key : 'Not set'}</Badge>
          <Badge variant="outline" data-testid="voice-ai-keys-llm-summary">LLM Key: {settings.credentials_summary?.has_llm_api_key ? settings.credentials_summary?.masked_llm_api_key : 'Not set'}</Badge>
        </div>

        {!featureEnabled && (
          <div className="border rounded p-3 bg-amber-50 border-amber-300" data-testid="voice-ai-keys-feature-warning">
            <p className="text-sm text-amber-800 flex items-center gap-2"><AlertTriangle className="w-4 h-4" /> Enable Voice AI Lead Generation in Dev Settings → Features first.</p>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <div>
            <Label>Voice Provider</Label>
            <Select value={settings.provider} onValueChange={(value) => setSettings((p) => ({ ...p, provider: value }))}>
              <SelectTrigger data-testid="voice-ai-keys-provider-select"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="elevenlabs">ElevenLabs</SelectItem></SelectContent>
            </Select>
          </div>
          <div>
            <Label>LLM Provider</Label>
            <Select value={settings.llm_provider} onValueChange={(value) => setSettings((p) => ({ ...p, llm_provider: value }))}>
              <SelectTrigger data-testid="voice-ai-keys-llm-provider-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="openai">OpenAI</SelectItem>
                <SelectItem value="anthropic">Anthropic</SelectItem>
                <SelectItem value="gemini">Gemini</SelectItem>
                <SelectItem value="custom">Custom</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>LLM Model</Label>
            <Input data-testid="voice-ai-keys-llm-model-input" value={settings.llm_model} onChange={(e) => setSettings((p) => ({ ...p, llm_model: e.target.value }))} />
          </div>
          <div>
            <Label>Live Transfer Number</Label>
            <Input data-testid="voice-ai-keys-transfer-number-input" value={settings.live_transfer_number} onChange={(e) => setSettings((p) => ({ ...p, live_transfer_number: e.target.value }))} />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <Label>ElevenLabs API Key (optional update)</Label>
            <Input type="password" data-testid="voice-ai-keys-elevenlabs-input" value={settings.credentials_input.elevenlabs_api_key} onChange={(e) => setSettings((p) => ({ ...p, credentials_input: { ...p.credentials_input, elevenlabs_api_key: e.target.value } }))} />
          </div>
          <div>
            <Label>Primary LLM API Key (optional update)</Label>
            <Input type="password" data-testid="voice-ai-keys-llm-input" value={settings.credentials_input.llm_api_key} onChange={(e) => setSettings((p) => ({ ...p, credentials_input: { ...p.credentials_input, llm_api_key: e.target.value } }))} />
          </div>
        </div>

        <div className="space-y-2" data-testid="voice-ai-keys-additional-providers">
          <div className="flex items-center justify-between">
            <Label>Additional Providers</Label>
            <Button variant="outline" size="sm" onClick={addProvider} data-testid="voice-ai-keys-add-provider-button"><Plus className="w-3 h-3 mr-1" />Add</Button>
          </div>
          {settings.additional_providers.map((row, idx) => (
            <div key={`keys-provider-${idx}`} className="grid grid-cols-1 md:grid-cols-4 gap-2" data-testid={`voice-ai-keys-provider-row-${idx}`}>
              <Input value={row.provider_name || ''} placeholder="Provider" data-testid={`voice-ai-keys-provider-name-${idx}`} onChange={(e) => setSettings((p) => ({ ...p, additional_providers: p.additional_providers.map((it, i) => i === idx ? { ...it, provider_name: e.target.value } : it) }))} />
              <Select value={row.provider_type || 'llm'} onValueChange={(value) => setSettings((p) => ({ ...p, additional_providers: p.additional_providers.map((it, i) => i === idx ? { ...it, provider_type: value } : it) }))}>
                <SelectTrigger data-testid={`voice-ai-keys-provider-type-${idx}`}><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="llm">LLM</SelectItem><SelectItem value="voice">Voice</SelectItem><SelectItem value="other">Other</SelectItem></SelectContent>
              </Select>
              <Input type="password" placeholder={row.masked_api_key || 'API key'} data-testid={`voice-ai-keys-provider-key-${idx}`} value={row.api_key || ''} onChange={(e) => setSettings((p) => ({ ...p, additional_providers: p.additional_providers.map((it, i) => i === idx ? { ...it, api_key: e.target.value } : it) }))} />
              <Button variant="ghost" data-testid={`voice-ai-keys-provider-remove-${idx}`} onClick={() => removeProvider(idx)}><Trash2 className="w-4 h-4" /></Button>
            </div>
          ))}
        </div>

        <Button onClick={saveSettings} disabled={saving || !featureEnabled} data-testid="voice-ai-keys-save-button">
          {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />} Save Voice AI Key Settings
        </Button>
      </CardContent>
    </Card>
  );
}
